import { defineStore } from 'pinia'
import type { MfaStep2Payload, MfaMethodSummary } from '@/types/type'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null as string | null,
    refreshToken: null as string | null,
    tokenData: null as any,
    refreshTokenData: null as any,
    user: null as Record<string, any> | null,
    roleId: null as number | null,
    UserId: null as number | null,
    reportsPermissions:[] as { id: string; name: string; description: string }[],
    reauthRequired: false as boolean,
    hasPmsSubscription: null as boolean | null,
    pmsSubscription: null as any,
    mfaPending: false as boolean,
    mfaChallengeId: null as string | null,
    mfaAvailableMethods: [] as MfaMethodSummary[],
    mfaDefaultMethodId: null as number | null,
    rememberedEmail: null as string | null,
    mfaHasRecoveryCodes: false,
  }),

  getters: {
    isLoggedIn: (state) => !!state.user,
    isFullyAuthenticated: (state) => !!(state.token && state.user && state.UserId),// && state.roleId
    getUser: (state) => state.user,
    isAuthenticated: (state) => !!state.token,
    isPmsSubscriptionBlocked: (state) => state.hasPmsSubscription === false,
  },

  actions: {

    login(user: any, token: string) {
      this.user = { ...user };
      this.token = token;
      this.roleId = user.roleId;
      this.UserId = user.UserId;
    },

    setReauthRequired(flag: boolean) {
      this.reauthRequired = flag
    },

    setPmsSubscriptionInfo(hasPmsSubscription: boolean | null, pmsSubscription: any | null) {
      this.hasPmsSubscription = hasPmsSubscription
      this.pmsSubscription = pmsSubscription
    },

    logout() {
      this.token = null;
      this.refreshToken = null;
      this.user = null;
      this.roleId = null;
      this.UserId = null;
      this.token = null
      this.refreshToken = null
      this.tokenData = null
      this.refreshTokenData = null
      this.reauthRequired = false
      this.hasPmsSubscription = null
      this.pmsSubscription = null
    },

    forceLogout() {

      this.$reset();
      this.token = null;
      this.refreshToken = null;
      this.user = null;
      this.roleId = null;
      this.UserId = null;
      this.reauthRequired = false;
      this.hasPmsSubscription = null
      this.pmsSubscription = null

    },

    setRoleId(roleId: number) {
      this.roleId = roleId;
    },
    setUserId(UserId: number) {
      this.UserId = UserId;
    },
    setReportsPermissions(permissions:any){
       this.reportsPermissions = permissions;
    },
     updateToken(token: string, tokenData: any) {
      this.token = token
      this.tokenData = tokenData
    },

    // Met à jour le refresh token et ses données
    updateRefreshToken(refreshToken: string, refreshTokenData: any) {
      this.refreshToken = refreshToken
      this.refreshTokenData = refreshTokenData
    },

    /**
     * Vérifie si l'utilisateur a une ou plusieurs permissions
     * @param names string | string[]
     */
    hasReportPermission(permissionName: string) {
      try {
        // Si l'utilisateur n'a pas de permissions définies, on refuse l'accès
        if (!this.user?.permisReports) {
          console.warn('Aucune permission définie pour l\'utilisateur');
          return false;
        }

        // Si permisReports est déjà un tableau, on l'utilise directement
        if (Array.isArray(this.user.permisReports)) {
          return this.user.permisReports.includes(permissionName);
        }

        // Sinon, on essaie de le parser comme une chaîne JSON
        if (typeof this.user.permisReports === 'string') {
          const permissions = JSON.parse(this.user.permisReports);
          return Array.isArray(permissions) && permissions.includes(permissionName);
        }

        console.warn('Format de permissions non reconnu:', this.user.permisReports);
        return false;
      } catch (error) {
        console.error('Erreur lors de la vérification des permissions:', error, 'Permission demandée:', permissionName);
        return false;
      }
    },
    hasPermission(permissionName: string) {
      if (!this.user?.permisPrivileges) return false;

      try {
        const permissions = JSON.parse(this.user.permisPrivileges);
        return permissions.includes(permissionName);
      } catch (error) {
        console.error('Erreur lors de la vérification des permissions:', error);
        return false;
      }
    },
    clearsetRoleId() {
      this.roleId = null;
    },
    clearsetUserId() {
      this.UserId = null;
    },
    clearsetUser() {
      this.user = null;
    },
    updateUser(partialUser: Record<string, any>) {
     if (this.user) {
       this.user = { ...this.user, ...partialUser }
      }
    },
     clearPermissionsReports() {
      this.reportsPermissions = []
    },

    setPendingMfa(payload: MfaStep2Payload, rememberedEmail?: string) {
      this.mfaPending = true
      this.mfaChallengeId = payload.mfa_challenge_id
      this.mfaAvailableMethods = payload.available_methods
      this.mfaDefaultMethodId = payload.default_method_id
      this.mfaHasRecoveryCodes = (payload as any).has_recovery_codes ?? false
      if (rememberedEmail) this.rememberedEmail = rememberedEmail
    },

    clearPendingMfa() {
      this.mfaPending = false
      this.mfaChallengeId = null
      this.mfaAvailableMethods = []
      this.mfaDefaultMethodId = null
      this.mfaHasRecoveryCodes = false
    },

    abortMfa() {
      this.clearPendingMfa()
      this.rememberedEmail = null
    },

    hydrateMfaFromProfile(profile: { methods: any[]; mfa_enabled_overall?: boolean; has_recovery_codes?: boolean }) {
      const methods = Array.isArray(profile?.methods) ? profile.methods : []
      this.mfaAvailableMethods = methods.map((m: any) => ({
        id: m.id,
        type: m.type,
        is_default: !!m.is_default,
        is_verified: !!m.is_verified,
        label: m.label ?? null,
        last_used_at: m.last_used_at ?? null,
      }))
      const defaultMethod = methods.find((m: any) => m.is_default)
      this.mfaDefaultMethodId = defaultMethod?.id ?? null
      if (this.user && profile.mfa_enabled_overall !== undefined) {
        this.user = { ...this.user, mfa_enabled_overall: profile.mfa_enabled_overall }
      }
      if (this.user && profile.has_recovery_codes !== undefined) {
        this.user = { ...this.user, has_mfa_recovery: profile.has_recovery_codes }
      }
    },
  },

  persist: {
    pick: [
      'token',
      'refreshToken',
      'tokenData',
      'refreshTokenData',
      'user',
      'roleId',
      'UserId',
      'reportsPermissions',
      'reauthRequired',
      'hasPmsSubscription',
      'pmsSubscription',
      'rememberedEmail',
      'mfaPending',
      'mfaChallengeId',
      'mfaAvailableMethods',
      'mfaDefaultMethodId',
    ],
  },
});
